import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import './crop.css'

const tiles = ['Top left', 'Top right', 'Bottom left', 'Bottom right']

interface CropOverlayProps {
  videos: RefObject<(HTMLVideoElement | null)[]>
  active: number
  source: string
}

/** All views share one decoder, clock and audio track. No pixel readback is needed,
 * so video served without canvas CORS permissions can still be displayed. */
export function CropOverlay({ videos, active, source }: CropOverlayProps) {
  // 0–3 are crops; 4 is the complete OBS composition.
  const [primary, setPrimary] = useState(4)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const drawerId = useId()
  const drawerHandle = useRef<HTMLButtonElement>(null)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const canvases = useRef<(HTMLCanvasElement | null)[]>([])

  useEffect(() => {
    const video = videos.current[active]
    setReady(false)
    setFailed(false)
    if (!video || !source) return
    // Include iOS browsers, which also use WebKit, in the conservative cap.
    const userAgent = navigator.userAgent
    const isWebKit =
      /AppleWebKit/i.test(userAgent) &&
      (!/(Chrome|Chromium|Android)/i.test(userAgent) ||
        /iPad|iPhone|iPod/i.test(userAgent))
    const previewFrameInterval = 1000 / (isWebKit ? 8 : 24)
    let frame = 0
    let animation = 0
    let stopped = false
    let painted = false
    let lastPreviewFrame = -Infinity
    const expectedSource = new URL(source, location.href).href
    const targets = canvases.current.flatMap((canvas, position) => {
      if (position < 4 && !drawerOpen) return []
      const tile = position === 4 ? primary : position === primary ? 4 : position
      const context = canvas?.getContext('2d', { alpha: false })
      return canvas && context ? [{ canvas, context, tile, preview: position < 4 }] : []
    })
    function draw(force = false) {
      if (stopped || document.hidden) return
      if (video!.currentSrc !== expectedSource || video!.readyState < 2) return
      const width = video!.videoWidth / 2
      const height = video!.videoHeight / 2
      if (!width || !height) return
      const now = performance.now()
      const paintPreviews = force || now - lastPreviewFrame >= previewFrameInterval
      try {
        for (const { canvas, context, tile, preview } of targets) {
           // Keep WebKit's costly video-to-canvas copies at a lower cadence.
           // The main view always follows every video frame.
          if (preview && !paintPreviews) continue
          context.drawImage(
            video!,
            tile === 4 ? 0 : (tile % 2) * width,
            tile === 4 ? 0 : Math.floor(tile / 2) * height,
            tile === 4 ? width * 2 : width,
            tile === 4 ? height * 2 : height,
            0,
            0,
            canvas.width,
            canvas.height,
          )
        }
        if (paintPreviews) {
          // Preserve the remainder so a 30 fps source can deliver 24 fps previews
          // instead of rounding every interval up to two frames (15 fps).
          lastPreviewFrame =
            force || !Number.isFinite(lastPreviewFrame)
              ? now
              : now - ((now - lastPreviewFrame) % previewFrameInterval)
        }
        if (!painted) {
          painted = true
          setReady(true)
        }
      } catch {
        // Keep the native video usable if the browser cannot draw this source.
        stopped = true
        setFailed(true)
      }
    }
    function resize() {
      for (const { canvas } of targets) {
        const bounds = canvas.getBoundingClientRect()
        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        const aspect =
          video!.videoWidth && video!.videoHeight
            ? video!.videoWidth / video!.videoHeight
            : 16 / 9
        const width = Math.max(
          1,
          Math.min(
            video!.videoWidth / 2 || 1920,
            Math.round(Math.min(bounds.width, bounds.height * aspect) * ratio),
          ),
        )
        const height = Math.max(1, Math.round(width / aspect))
        if (canvas.width !== width) canvas.width = width
        if (canvas.height !== height) canvas.height = height
      }
      draw(true)
    }
    const observer = new ResizeObserver(resize)
    targets.forEach(({ canvas }) => observer.observe(canvas))
    function cancel() {
      if (frame) video!.cancelVideoFrameCallback(frame)
      cancelAnimationFrame(animation)
      frame = animation = 0
    }
    function tick() {
      frame = animation = 0
      draw()
      schedule()
    }
    function schedule() {
      if (
        stopped ||
        !targets.length ||
        document.hidden ||
        video!.paused ||
        video!.ended ||
        frame ||
        animation
      )
        return
      if (typeof video!.requestVideoFrameCallback === 'function')
        frame = video!.requestVideoFrameCallback(tick)
      else animation = requestAnimationFrame(tick)
    }
    function update() {
      draw(true)
      schedule()
    }
    function visibility() {
      cancel()
      if (!document.hidden) update()
    }
    function reset() {
      painted = false
      setReady(false)
    }
    const events = ['loadeddata', 'seeked', 'pause', 'playing'] as const
    events.forEach((event) => video.addEventListener(event, update))
    video.addEventListener('resize', resize)
    video.addEventListener('emptied', reset)
    document.addEventListener('visibilitychange', visibility)
    resize()
    schedule()
    return () => {
      stopped = true
      cancel()
      observer.disconnect()
      events.forEach((event) => video.removeEventListener(event, update))
      video.removeEventListener('resize', resize)
      video.removeEventListener('emptied', reset)
      document.removeEventListener('visibilitychange', visibility)
    }
  }, [videos, active, source, primary, drawerOpen])

  if (!source) return null

  return (
    <div className={`crop-overlay ${ready && !failed ? 'crop-ready' : ''}`}>
      {primary !== 4 && !failed && (
        <div className="crop-primary">
          <canvas
            ref={(node) => {
              canvases.current[4] = node
            }}
            role="img"
            aria-label={`${tiles[primary]} view`}
          />
        </div>
      )}
      {failed && (
        <span className="crop-failure" role="status">
          Angle previews unavailable. Showing all four views.
        </span>
      )}
      <div
        className={`crop-drawer ${drawerOpen ? 'is-open' : ''}`}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && drawerOpen) {
            event.preventDefault()
            event.stopPropagation()
            setDrawerOpen(false)
            drawerHandle.current?.focus({ preventScroll: true })
          }
        }}
      >
        <button
          ref={drawerHandle}
          className="crop-drawer-handle"
          type="button"
          aria-label={drawerOpen ? 'Close view drawer' : 'Open view drawer'}
          aria-expanded={drawerOpen}
          aria-controls={drawerId}
          title={drawerOpen ? 'Close views' : 'Choose view'}
          onClick={() => setDrawerOpen(!drawerOpen)}
        >
          {drawerOpen ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
        <div
          id={drawerId}
          className="crop-previews"
          role="group"
          aria-label="Choose primary view"
          inert={!drawerOpen}
          aria-hidden={!drawerOpen}
        >
          {!failed &&
            tiles.map((label, position) => {
              const tile = position === primary ? 4 : position
              const name = tile === 4 ? 'Quad' : label
              return (
                <button
                  type="button"
                  key={position}
                  aria-label={`Show ${name.toLowerCase()} as primary view`}
                  onClick={() => {
                    setPrimary(tile)
                  }}
                >
                  <canvas
                    ref={(node) => {
                      canvases.current[position] = node
                    }}
                    aria-hidden="true"
                  />
                  <span>{name}</span>
                </button>
              )
            })}
        </div>
      </div>
      <span className="sr-only" role="status">
        {primary === 4
          ? 'Quad is the primary view'
          : `${tiles[primary]} is the primary view`}
      </span>
    </div>
  )
}
